import { useState } from "react";

// src/hooks/useForm.ts
export type FormValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | File
  | File[]
  | string[]
  | number[];

export function useFormController<T extends object>(initialValues: T) {
  const [values, setValues] = useState<T>(initialValues);

  /** 🔥 Universal cleaner (fixes your bug permanently) */
  const cleanArray = (val: any): any[] => {
    if (!Array.isArray(val)) return [];
    return val.filter(v => v !== "" && v !== null && v !== undefined);
  };

  /** Generic setter for ANY field */
  const setField = <K extends keyof T>(name: K, value: T[K]) => {
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /** For <input />, <textarea /> */
  const handleInputChange =
    <K extends keyof T>(name: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField(name, e.target.value as T[K]);
    };

  /** For checkbox (boolean) */
  const handleCheckboxChange =
    <K extends keyof T>(name: K) =>
    (checked: boolean) => {
      setField(name, checked as T[K]);
    };

  /** For dropdowns, radios, multiselects */
  const handleValueChange =
    <K extends keyof T>(name: K) =>
    (value: T[K]) => {
      setField(name, value);
    };

  /** ✅ FIXED: Required value handler (safe for both single + array) */
  const handleRequiredValueChange =
    <K extends keyof T>(name: K) =>
    (value: any) => {
      if (value === undefined || value === null) {
        setField(name, ([] as unknown) as T[K]);
        return;
      }

      // ✅ If array → CLEAN IT (THIS FIXES YOUR ISSUE)
      if (Array.isArray(value)) {
        setField(name, cleanArray(value) as T[K]);
        return;
      }

      // ✅ If object → extract id (optional)
      if (typeof value === "object" && "id" in value) {
        setField(name, (value as any).id as T[K]);
        return;
      }

      // ✅ Normal value
      setField(name, value);
    };

  /** ✅ STRICT for DropdownMulti (use this one) */
  const handleRequiredValueChangeForDD =
    <K extends keyof T>(name: K) =>
    (val: any[]) => {
      setField(name, cleanArray(val) as T[K]);
    };

  /** For file & image inputs */
  const handleFilesChange =
    <K extends keyof T>(name: K) =>
    (files: File | File[]) => {
      setField(name, files as T[K]);
    };

  /** For Single file */
  const handleSingleFileChange =
    <K extends keyof T>(name: K) =>
    (file: File | undefined) => {
      setField(name, (file ?? null) as T[K]);
    };

  /** Overwrite full form (API use) */
  const setAllValues = (newValues: T) => {
    // 🔥 CLEAN ALL ARRAY FIELDS AUTOMATICALLY
    const cleaned: any = {};

    Object.keys(newValues).forEach((key) => {
      const val = (newValues as any)[key];
      cleaned[key] = Array.isArray(val) ? cleanArray(val) : val;
    });

    setValues(cleaned);
  };

  const resetForm = () => setValues(initialValues);

  return {
    values,
    setField,
    setAllValues,
    handleInputChange,
    handleCheckboxChange,
    handleValueChange,
    handleRequiredValueChange,
    handleRequiredValueChangeForDD,
    handleFilesChange,
    handleSingleFileChange,
    resetForm,
  };
}